import { useState } from 'react'
import { ReactComponent as IconPencil } from 'bootstrap-icons/icons/pencil.svg'
import { ReactComponent as IconPlus } from 'bootstrap-icons/icons/plus-circle.svg'
import { ReactComponent as IconLock } from 'bootstrap-icons/icons/lock.svg'
import { ReactComponent as IconArrowRepeat } from 'bootstrap-icons/icons/arrow-repeat.svg'

import { formatDate } from 'config/i18n'
import { toDisplayDate } from './date'
import { computeCalculated } from './calc'
import { useFormRecords } from './hooks'
import CustomFormRecordEditor, { isEditable } from './CustomFormRecordEditor'
import styles from './styles.module.scss'

const resolveChoiceLabel = (col, raw) => {
  const options = col.options || []
  const match = options.find((opt) => {
    const val = opt.value ?? opt.label ?? opt
    return val === raw || String(val) === String(raw)
  })
  if (match) return match.label ?? match.value ?? match
  // Registros viejos con slug: "foo_bar" → buscar label slugificado
  const slug = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, '_')
  const bySlug = options.find((opt) => slug(opt.label ?? opt) === String(raw))
  if (bySlug) return bySlug.label ?? bySlug
  return raw
}

const cellValue = (col, record, columns) => {
  if (col.type === 'calculated') {
    const stored = record.data?.[col.id]
    return stored != null && stored !== '' ? stored : computeCalculated(col, columns, record.data || {})
  }
  const v = record.data?.[col.id]
  if (v == null || v === '') return ''
  if (col.type === 'boolean') return v ? 'Sí' : 'No'
  if (col.type === 'single_choice' || col.type === 'multiple_choice') {
    if (Array.isArray(v)) return v.map((x) => resolveChoiceLabel(col, x)).join(', ')
    return resolveChoiceLabel(col, v)
  }
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

// recordDate es 'YYYY-MM-DD' (fecha del control); createdAt (fallback) es epoch,
// y postgres.js lo devuelve como string — de ahí el Number(ts) || ts.
const dateLabel = (record) => {
  const ts = record.recordDate || record.createdAt
  return ts ? toDisplayDate(Number(ts) || ts) : '—'
}

const dateTooltip = (record) => {
  const createdAt = record.createdAt ? Number(record.createdAt) : null
  const author = record.createdByName || record.createdBy || record.createdByResourceID || '—'
  const when = createdAt
    ? formatDate(createdAt, 'dd-MMM-yyyy HH:mm')
    : null
  const parts = []
  if (when) parts.push(`Creado: ${when}`)
  parts.push(`Autor: ${author}`)
  return parts.join('\n')
}

const isNumeric = (col) => col.type === 'number' || col.type === 'scale' || col.type === 'calculated'

// Un número cargado por fuera del min/max definido en el campo (los valores
// anormales no se bloquean, pero se destacan en la grilla para que salten a la vista).
const isOutOfRange = (col, v) => {
  if (col.type !== 'number' || v == null || v === '') return false
  const n = Number(v)
  if (!Number.isFinite(n)) return false
  return (Number.isFinite(col.min) && n < col.min) || (Number.isFinite(col.max) && n > col.max)
}
const rangeText = (col) => {
  const hasMin = Number.isFinite(col.min)
  const hasMax = Number.isFinite(col.max)
  return hasMin && hasMax ? `${col.min}–${col.max}` : hasMin ? `≥ ${col.min}` : hasMax ? `≤ ${col.max}` : ''
}

function ParagraphCell({ text }) {
  const [open, setOpen] = useState(false)
  const value = String(text || '')
  const long = value.length > 80 || value.split('\n').length > 2
  if (!text) return <span className={styles.note}>—</span>
  return (
    <div
      className={`${styles.paraCell} ${long ? styles.paraTruncatable : ''} ${open ? styles.paraOpen : styles.paraClamp}`}
      title={long && !open ? value : undefined}
      onClick={() => { if (long) setOpen((o) => !o) }}
      role={long ? 'button' : undefined}
      tabIndex={long ? 0 : undefined}
      onKeyDown={(e) => {
        if (!long) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o) }
      }}
    >
      <div className={styles.paraText}>{value}</div>
      {long && !open && <span className={styles.paraHint}>Click para ver más</span>}
      {long && open && <span className={styles.paraHint}>Click para achicar</span>}
    </div>
  )
}

function CustomFormGrid({ form, consumerID, resource, user, canManage, onEdit }) {
  const columns = (form.columns || []).filter((c) => !c.hidden).slice().sort((a, b) => (a.order || 0) - (b.order || 0))
  const records = useFormRecords(form.id, consumerID)
  const allRows = records.data || []
  const activeRows = allRows.filter((r) => !r.deleted)
  const deletedRows = allRows.filter((r) => r.deleted)
  // Grilla ancha: nueva página en horizontal al imprimir (ver .landscapePrint / @page en styles.module.scss).
  const isWide = columns.length > 6

  const [editing, setEditing] = useState(null) // { record } | { new: true } | null
  const [showDeleted, setShowDeleted] = useState(false)
  // Eje de la tabla (como la rotación de la HCE v1):
  //   'horizontal' → registros = filas, campos = columnas
  //   'vertical'   → campos = filas, registros = columnas (mejor con muchos campos)
  // Con muchos campos arranca en vertical para no romper la UI a lo ancho.
  const [orientation, setOrientation] = useState(isWide ? 'vertical' : 'horizontal')
  const isVertical = orientation === 'vertical'

  const closeEditor = () => setEditing(null)
  const onSaved = () => { setEditing(null); records.refetch() }

  // Form fuera de alcance/deshabilitado que se muestra solo porque tiene cargas de
  // este paciente: se ven los datos, pero no se pueden agregar controles nuevos.
  const canAddRecords = form.canAddRecords !== false

  // "Solo yo" se lee desde la perspectiva del creador; para otro profesional que
  // ve el form (porque tiene cargas) el texto correcto es "Solo el autor".
  const isOwner = resource?.id && form.ownerResourceID === resource.id

  const chips = []
  if (!canAddRecords) chips.push(['chipOff', 'Solo lectura'])
  if (form.professionals == null) chips.push(['chipGray', 'Todos los profesionales'])
  else chips.push(['chipBrand', isOwner ? 'Solo yo' : 'Solo el autor'])
  if (form.specialties == null) chips.push(['chipGray', 'Todas las especialidades'])
  else form.specialties.forEach((s) => chips.push(['chipSpec', String(s).replace('pms_specialties:', '')]))

  const dateHeader = (record) => (
    <button
      type="button"
      className={styles.date}
      onClick={() => setEditing({ record })}
      title={dateTooltip(record)}
    >
      {!isEditable(record, user, resource) && <IconLock width="11" height="11" className="me-1 text-secondary" />}
      <span>{dateLabel(record)}</span>
    </button>
  )
  const cell = (col, record) => {
    if (col.type === 'file') {
      const urls = (Array.isArray(record.data?.[col.id]) ? record.data[col.id] : []).filter((u) => typeof u === 'string' && u)
      return (
        <td key={`${col.id}-${record.id}`}>
          {urls.length
            ? urls.map((url) => (
                <div key={url}>
                  <a href={url} target="_blank" rel="noopener noreferrer" className={styles.fileLink}>{url.replace('https://', '')}</a>
                </div>
              ))
            : <span className={styles.note}>—</span>}
        </td>
      )
    }
    const raw = cellValue(col, record, columns)
    const outOfRange = isOutOfRange(col, record.data?.[col.id])
    return (
      <td
        key={`${col.id}-${record.id}`}
        className={`${isNumeric(col) ? styles.num : ''} ${col.type === 'calculated' ? styles.calcCell : ''}`}
        style={outOfRange ? { color: '#c0392b', fontWeight: 700 } : undefined}
        title={outOfRange ? `Fuera del rango normal (${rangeText(col)})` : undefined}
      >
        {col.type === 'paragraph'
          ? <ParagraphCell text={raw} />
          : (raw || <span className={styles.note}>—</span>)}
      </td>
    )
  }
  const dateHeaderCell = (record) => (
    <>
      {dateHeader(record)}
      {record.deleted && <span className={`${styles.chip} ${styles.chipOff}`} style={{ marginLeft: 6 }}>Eliminado</span>}
    </>
  )

  // Registros a mostrar (activos + eliminados si el toggle está prendido).
  const rows = showDeleted ? [...activeRows, ...deletedRows] : activeRows

  return (
    <div className={`${styles.card} ${isWide && !isVertical ? styles.landscapePrint : ''}`}>
      <div className={styles.cardHead}>
        {allRows.length > 0 && (
          <button
            type="button"
            className={`${styles.iconbtn} d-print-none`}
            onClick={() => setOrientation((o) => (o === 'vertical' ? 'horizontal' : 'vertical'))}
            title={isVertical ? 'Ver registros en filas' : 'Ver registros en columnas'}
            aria-label="Rotar tabla"
          >
            <IconArrowRepeat width="14" height="14" />
          </button>
        )}
        <span className={styles.cardTitle}>{form.name}</span>
        {chips.map(([cls, text], i) => (<span key={i} className={`${styles.chip} ${styles[cls]}`}>{text}</span>))}
        <div className={styles.spacer} />
        {deletedRows.length > 0 && (
          <button type="button" className={`${styles.btn} ${styles.ghost} d-print-none`} onClick={() => setShowDeleted((v) => !v)}>
            {showDeleted ? 'Ocultar eliminados' : `Ver eliminados (${deletedRows.length})`}
          </button>
        )}
        {canAddRecords && (
          <button type="button" className={`${styles.btn} ${styles.outline} d-print-none`} onClick={() => setEditing({ new: true })}>
            <IconPlus width="13" height="13" /> Control
          </button>
        )}
        {canManage && (
          <button type="button" className={`${styles.iconbtn} d-print-none`} onClick={() => onEdit(form)} title="Editar formulario"><IconPencil width="14" height="14" /></button>
        )}
      </div>

      <div className={styles.gridWrap}>
        {rows.length === 0 ? (
          <div className={styles.empty} style={{ padding: '.75rem' }}>
            {deletedRows.length ? 'No hay controles activos.' : 'Sin controles cargados todavía.'}
          </div>
        ) : isVertical ? (
          // Campos = filas, registros = columnas (mejor con muchos campos).
          <table className={styles.grid}>
            <tbody>
              <tr>
                <td className={styles.label}>Fecha</td>
                {rows.map((r) => (
                  <td key={r.id} className="text-center" style={r.deleted ? { opacity: 0.55 } : undefined}>
                    {dateHeaderCell(r)}
                  </td>
                ))}
              </tr>
              {columns.map((col) => (
                <tr key={col.id}>
                  <td className={styles.label}>{col.label}{col.unit ? ` (${col.unit})` : ''}</td>
                  {rows.map((r) => cell(col, r))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // Registros = filas, campos = columnas (por defecto).
          <table className={styles.grid}>
            <tbody>
              <tr>
                <td className={styles.label}>Fecha</td>
                {columns.map((col) => (<td key={col.id} className={styles.label}>{col.label}{col.unit ? ` (${col.unit})` : ''}</td>))}
              </tr>
              {rows.map((r) => (
                <tr key={r.id} style={r.deleted ? { opacity: 0.55 } : undefined}>
                  <td className="text-center">{dateHeaderCell(r)}</td>
                  {columns.map((col) => cell(col, r))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <CustomFormRecordEditor
          form={form}
          consumerID={consumerID}
          record={editing.record}
          resource={resource}
          user={user}
          onClose={closeEditor}
          onSaved={onSaved}
        />
      )}
    </div>
  )
}

export default CustomFormGrid
